import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  ValidatorFn,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogClose,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { AxiosError } from 'axios';
import {
  RunTransition,
  RunTransitionWithDialog,
  WorkflowService,
} from '../../services/workflow/workflow.service';
import { claimSumRemainingAmount } from 'src/types/claim-sum';
import { FullPaymentWarningComponent } from '../full-payment-warning/full-payment-warning.component';
import { PayeePopupComponent } from '../../payee-popup/payee-popup.component';
import { PayeeIconComponent } from '../../payee-icon/payee-icon.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PayeeFormFieldComponent } from '../../payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeInputDirective } from '../../payee-input/payee-input.directive';
import { MatError } from '@angular/material/input';
import { PayeeDropdownComponent } from '../../payee-form-fields/payee-dropdown/payee-dropdown.component';
import { PayeeButtonComponent } from '../../payee-buttons/payee-button/payee-button.component';
import { DecimalPipe } from '@angular/common';

type DialogData = {
  transition_ids: {
    full_payment: string;
    partial_payment: string;
  };
  claims: {
    is_paid: boolean;
    paid_amount: number;
    original_amount: number;
    disputed_amount: number;
    currency_iso: string;
    claim_type_label: string;
    optional_claims: {
      paid_amount: number;
      original_amount: number;
      disputed_amount: number;
      claim_type_label: string;
    }[];
  }[];
};

@Component({
  selector: 'app-full-payment-warning',
  templateUrl: './manual-payment.component.html',
  styleUrls: ['./manual-payment.component.scss'],
  imports: [
    PayeePopupComponent,
    PayeeIconComponent,
    MatProgressSpinner,
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    PayeeInputDirective,
    MatError,
    PayeeDropdownComponent,
    PayeeButtonComponent,
    MatDialogClose,
    DecimalPipe,
    TranslateModule,
  ],
})
export class ManualPaymentComponent implements OnInit, OnDestroy {
  readonly claimRemainingAmount = claimSumRemainingAmount;
  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);
  private dialogRef =
    inject<MatDialogRef<ManualPaymentComponent>>(MatDialogRef);
  private data = inject<RunTransitionWithDialog>(MAT_DIALOG_DATA);
  private workflowService = inject(WorkflowService);
  private snackbar = inject(MatSnackBar);
  private translate = inject(TranslateService);

  private readonly destory = new Subject<void>();
  dialogData!: DialogData;
  readonly dataLoading = signal(false);
  loading = false;

  private currencyAmounts: Record<string, number> = {};
  currencyOptions: string[] = [];

  readonly manualPaymentForm = this.fb.group({
    paymentAmount: this.fb.control<string | number | null>(null),
    currency: this.fb.control<string | null>(null),
  });

  get paymentAmount() {
    return this.manualPaymentForm.controls.paymentAmount;
  }
  get currency() {
    return this.manualPaymentForm.controls.currency;
  }

  get isFullPayment(): boolean {
    if (!this.currency.value || !this.paymentAmount.value) {
      return false;
    }

    const amount: number = +this.paymentAmount.value
      .toString()
      .replace(/\D/g, '');
    const maxAmount: number = this.currencyAmounts[this.currency.value] ?? 0;

    return amount >= maxAmount;
  }

  async ngOnInit(): Promise<void> {
    await this.setDialogData();

    for (const claim of this.dialogData.claims) {
      if (!(claim.currency_iso in this.currencyAmounts)) {
        this.currencyAmounts[claim.currency_iso] = 0;
      }
      this.currencyAmounts[claim.currency_iso] +=
        claimSumRemainingAmount(claim);
      for (const optionalClaim of claim.optional_claims) {
        if (!(claim.currency_iso in this.currencyAmounts)) {
          this.currencyAmounts[claim.currency_iso] = 0;
        }
        this.currencyAmounts[claim.currency_iso] +=
          claimSumRemainingAmount(optionalClaim);
      }
    }

    this.currencyOptions = Array.from(
      new Set(
        this.dialogData.claims
          .filter((c) => !c.is_paid)
          .map((c) => c.currency_iso),
      ),
    );

    this.currency.valueChanges
      .pipe(takeUntil(this.destory))
      .subscribe((currency) => {
        const validators = [Validators.required, this.numberValidator];

        if (currency) {
          validators.push(Validators.max(this.currencyAmounts[currency] ?? 0));
        }

        this.paymentAmount.setValidators(validators);
        this.paymentAmount.updateValueAndValidity();
        if (this.paymentAmount.hasError('max') && currency) {
          this.paymentAmount.patchValue(this.currencyAmounts[currency] ?? 0);
        }
      });

    this.currency.patchValue(
      this.dialogData.claims.find((c) => !c.is_paid)?.currency_iso ?? null,
    );
    if (this.currency.value) {
      this.paymentAmount.patchValue(
        this.currencyAmounts[this.currency.value] ?? 0,
      );
    }
  }

  ngOnDestroy(): void {
    this.destory.next();
    this.destory.complete();
  }

  readonly submit = async () => {
    if (this.manualPaymentForm.invalid || !this.dialogData) {
      Object.values(this.manualPaymentForm.controls).forEach((control) =>
        control.markAsDirty(),
      );
      return;
    }

    if (this.isFullPayment) {
      const confirmDialogRef = this.dialog.open(FullPaymentWarningComponent);
      const result = await confirmDialogRef.afterClosed().toPromise();
      if (!result) {
        return;
      }
    }

    const transitionId = this.isFullPayment
      ? this.dialogData.transition_ids.full_payment
      : this.dialogData.transition_ids.partial_payment;

    const { paymentAmount, currency } = this.manualPaymentForm.getRawValue();

    const amountValue =
      typeof paymentAmount === 'number'
        ? paymentAmount
        : parseInt(String(paymentAmount).replace(/\D/g, '') || '0');

    const closeValue: RunTransition = {
      caseId: this.data.caseId,
      transitionId,
      params: {
        ...(this.data.extraParams ?? {}),
        amount: amountValue,
        currency_iso: currency,
      },
    };
    this.loading = true;
    try {
      await this.workflowService.runTransition(closeValue);
      this.dialogRef.close(closeValue);
    } catch (error) {
      this.workflowService.showTransitionError(error);
    } finally {
      this.loading = false;
    }
  };

  private readonly numberValidator: ValidatorFn = (control) => {
    const value = control.value;
    const maxValue: number =
      this.currencyAmounts[this.currency.value ?? ''] ?? 0;
    if (!value) {
      if (value !== undefined && value !== null) {
        setTimeout(() => this.paymentAmount.patchValue(maxValue));
      }
      return null;
    }

    if (typeof value !== 'number' && typeof value !== 'string') {
      return {
        pattern: true,
      };
    }

    const numValue: number =
      typeof value === 'number'
        ? value
        : parseInt(value.replace(/\D/g, '') || '0');
    const formattedValue: string = numValue
      .toLocaleString('hu-HU')
      .replace(/,/g, ' ');
    if (formattedValue !== value) {
      setTimeout(() => this.paymentAmount.patchValue(formattedValue));
    } else if (numValue > maxValue) {
      setTimeout(() => this.paymentAmount.patchValue(maxValue));
    }

    return null;
  };

  private async setDialogData(): Promise<void> {
    try {
      this.dataLoading.set(true);
      this.dialogData = (await this.workflowService.getDialogData({
        caseId: this.data.caseId,
        transitionId: this.data.transitionId,
      })) as DialogData;
    } catch (error: AxiosError | unknown) {
      let message = this.translate.instant('ERROR_MESSAGES.UNKNOWN_ERROR');

      if (error instanceof AxiosError) {
        console.error(
          'Network error while getting dialog data',
          error.response?.data,
        );
        if (error.response?.data?.message) {
          message = error.response?.data?.message;
        }
      } else {
        console.error('Unknown error while getting dialog data', error);
      }
      this.snackbar.open(message, 'OK', {
        duration: 5000,
      });
      this.dialogRef.close();
      return;
    } finally {
      this.dataLoading.set(false);
    }
  }
}
